import { mapStoredMediaToWizardItem } from "./timeCapsuleMedia";

export function normalizeCapsuleType(value = "") {
  return String(value || "").trim().toLowerCase().replace(/[\s_-]+/g, "");
}

const FEATURED_CAPSULE_TYPES = new Set(["sponsored", "memberexclusive"]);

export function isFeaturedCapsuleType(type) {
  return FEATURED_CAPSULE_TYPES.has(normalizeCapsuleType(type));
}

export function isFeaturedForwardCapsule(capsule = {}) {
  const sourceId = String(
    capsule?.sourceCapsuleId || capsule?.forwardSourceId || capsule?.forwardedFromId || ""
  ).trim();
  return Boolean(sourceId) && isFeaturedCapsuleType(capsule?.forwardedFromType);
}

export function getForwardShareMode(capsule = {}) {
  const type = normalizeCapsuleType(capsule?.capsuleType || capsule?.type);
  if (type === "sponsored" || type === "memberexclusive") return "editable";
  if (type === "private" || type === "public") return "editable";
  return "native";
}

function toDateSafe(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }
  if (value instanceof Date) return value;
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  return null;
}

// Mirrors the server-side rule in functions/messages.js
// (getAvailableFeaturedSource): a forwarded featured source is only shown
// while it is still a live featured capsule. A missing doc means the
// business withdrew it.
export function getFeaturedSourceAvailability(sourceCapsule) {
  if (!sourceCapsule) return "unavailable";
  if (!isFeaturedCapsuleType(sourceCapsule?.capsuleType || sourceCapsule?.type)) {
    return "unavailable";
  }
  if (
    sourceCapsule?.isDraft === true ||
    String(sourceCapsule?.status || "").trim().toLowerCase() === "draft" ||
    String(sourceCapsule?.lifecycleStatus || "").trim().toLowerCase() === "draft"
  ) {
    return "unavailable";
  }
  const endDate = toDateSafe(sourceCapsule?.endDate);
  if (endDate && endDate.getTime() <= Date.now()) return "unavailable";
  return "available";
}

function isDraftCapsule(capsule = {}) {
  return (
    capsule?.lifecycleStatus === "draft" ||
    capsule?.status === "draft" ||
    capsule?.isDraft === true
  );
}

function isForwardedWizardCapsule({ forwardMode = "", capsule = {} } = {}) {
  return (
    forwardMode === "editable" ||
    Boolean(capsule?.forwardSourceId || capsule?.forwardedFromId)
  );
}

export function shouldUseFreshTimeCapsuleWizardSession({
  forwardMode = "",
  forceFreshSession,
  capsule = {},
} = {}) {
  if (isDraftCapsule(capsule)) return false;
  if (forceFreshSession === false) return false;
  return forceFreshSession === true || isForwardedWizardCapsule({ forwardMode, capsule });
}

export function mapForwardedMediaToWizardItem(media = {}) {
  const item = mapStoredMediaToWizardItem(media);
  const sourceStoragePath = String(
    media?.sourceStoragePath || media?.storagePath || item?.storagePath || ""
  ).trim();
  return {
    ...item,
    storagePath: "",
    draftOwnedMedia: false,
    forwardedSourceMedia: Boolean(item?.url),
    ...(sourceStoragePath ? { sourceStoragePath } : {}),
  };
}

export function buildForwardWizardCapsule(capsule = {}) {
  const location = capsule?.location && typeof capsule.location === "object"
    ? capsule.location
    : {};
  const sourceCapsuleId = String(capsule?.id || capsule?.capsuleId || "").trim();
  const isFeaturedSource = isFeaturedCapsuleType(capsule?.capsuleType || capsule?.type);
  // Featured (sponsored/memberExclusive) sources are referenced, never copied:
  // the envelope carries no source media or CTA — recipients resolve the
  // original capsule live after claiming.
  const media = !isFeaturedSource && Array.isArray(capsule?.media)
    ? capsule.media.map((item) => mapForwardedMediaToWizardItem(item)).filter((item) => item.url)
    : [];

  return {
    ...capsule,
    id: "",
    shareToken: undefined,
    shareUrl: undefined,
    token: undefined,
    lifecycleStatus: "",
    status: "",
    isDraft: false,
    audience: "share",
    sourceCapsuleId,
    forwardSourceId: sourceCapsuleId,
    forwardedFromId: sourceCapsuleId,
    forwardedFromType: String(capsule?.capsuleType || capsule?.type || "").trim(),
    media,
    ...(isFeaturedSource
      ? {
          messageBody: "",
          message: "",
          callToActionText: "",
          callToActionUrl: "",
          forwardSourceTitle: String(capsule?.messageTitle || "").trim(),
          forwardSourceAvatarUrl: String(capsule?.businessAvatarUrl || "").trim(),
        }
      : {}),
    location: {
      ...location,
      lat: Number(location?.lat),
      lng: Number(location?.lng),
      address: String(location?.address || "").trim(),
      placeId: String(location?.placeId || "").trim(),
      placeName: String(location?.placeName || capsule?.placeName || "").trim(),
    },
  };
}
